import type { PlayerManifest, Transition } from '@tbcast/shared';
import type { PlayerApi } from '../api';
import type { AssetCache } from '../cache';

/** Something that can be shown on screen for a while. */
export interface Renderable {
  el: HTMLElement;
  /** Resolves when the first frame can be shown without flicker (image decoded, video can play…). */
  ready(): Promise<void>;
  /** Called when the element becomes visible. */
  play(): void;
  /** Intrinsic duration (video length, presentation cycle) when known. */
  intrinsicDurationMs?: number;
  /** Notifies when the item ended on its own (video ended, presentation cycle finished). */
  onEnded?: (cb: () => void) => void;
  /** Releases everything (DOM, media decoders, timers). Must be idempotent. */
  destroy(): void;
}

export interface PlaybackContext {
  manifest: PlayerManifest;
  cache: AssetCache;
  api: PlayerApi;
  timeZone: string;
  locale: 'nl' | 'en';
  speed: number;
  volume: number;
  weatherTemp: () => number | null;
  /** Called whenever an item starts showing (proof of play). */
  onItemStart: (info: { kind: string; id: string; name?: string }) => () => void;
  /** Optional verbose logging. */
  log: (msg: string, data?: unknown) => void;
}

export interface SequenceItem {
  key: string;
  kind: 'media' | 'presentation' | 'widget';
  refId: string;
  durationMs: number;
  transition: Transition;
  validFrom?: string | null;
  validUntil?: string | null;
  dayparts?: Array<{ days?: number[]; startTime: string; endTime: string }>;
  /** For PDF/PPTX media: page index to show. */
  page?: number;
}

export function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(fallback);
      },
    );
  });
}
